import SubpageLayout from "@/components/SubpageLayout";
import { P, Section, Example, ContactCta, BackLink } from "@/components/Prose";

const Kurser = () => (
  <SubpageLayout overline="VI TILBYDER" title="Kurser og undervisning" art="/brand/statement-seeds.png" testId="page-service-kurser">
    <Section title="Viden skal kunne bruges i praksis">
      <P>
        Vi tilbyder kurser, oplæg og undervisningsforløb om traumer, traumeforståelse,
        traumeterapi og traumebevidst tilgang.
      </P>
      <P>
        Undervisning skal ikke kun give ny viden. Den skal skabe nye perspektiver og
        handlemuligheder i den praksis, deltagerne står i. Derfor tager vi ikke kun udgangspunkt i
        teori og traumefaglig viden, men også i den viden og erfaring, der allerede findes hos
        deltagerne og i organisationen. Vi kobler løbende faglig viden til konkrete situationer,
        målgrupper og dilemmaer og arbejder med at oversætte den til relevant praksis.
      </P>
      <P>
        Undervisningen kan stå alene eller indgå i længere kompetence- og implementeringsforløb.
      </P>
    </Section>

    <Section title="Eksempler på kurser og undervisning">
      <div className="space-y-8">
        <Example title="Introduktion til traumer og traumereaktioner" testId="course-example-introduktion">
          <P>
            Et grundlæggende kursus, der skaber et fælles traumefagligt fundament og
            begrebsapparat. Vi arbejder med forståelsen af traumereaktioner og deres betydning for
            adfærd, relationer og faglig praksis samt med belastende og beskyttende faktorer.
            Fokus er ikke kun på at forstå reaktionerne, men også på det, deltagerne selv kan
            påvirke gennem deres praksis for at reducere belastning og understøtte trivsel og
            udvikling.
          </P>
        </Example>

        <Example
          title="Introduktion til traumebevidst tilgang – ledelse og organisatorisk udvikling"
          subline="Målgruppe: Ledere, bestyrelser, udviklingskonsulenter og andre med ansvar for faglig og organisatorisk udvikling."
          testId="course-example-ledelse"
        >
          <P>
            Hvad taler vi egentlig om, når vi taler om traumer og traumebevidst tilgang? Hvordan
            forstås og arbejdes der med traumer på tværs af fagområder og sektorer, og hvad betyder
            de forskellige forståelsesrammer for de mennesker, der bevæger sig mellem systemerne?
          </P>
          <P>
            Vi introducerer til traumer, traumereaktioner og traumebevidst tilgang med særligt
            fokus på de forskellige faglige og organisatoriske sammenhænge, som mennesker indgår i.
            Vi undersøger, hvordan forskellige forståelser af menneskers reaktioner og behov kan få
            betydning for de indsatser, de tilbydes, og for samarbejdet på tværs.
          </P>
          <P>
            Med dette afsæt ser vi nærmere på, hvad traumebevidst tilgang kan betyde som
            organisatorisk tilgang. Hvilken rolle spiller ledelse, prioriteringer, faglige
            kulturer, arbejdsgange og organisatoriske rammer for mulighederne for at omsætte viden
            til praksis?
          </P>
          <P>
            Vi undersøger, hvordan organisationen kan skabe bedre betingelser for både de
            mennesker, den er sat i verden for at hjælpe, og de medarbejdere, der skal løfte
            opgaven. Her ser vi også på medarbejdernes faglige sikkerhed, trivsel og
            handlemuligheder, og hvordan traumebevidste principper kan få plads i en hverdag præget
            af økonomiske, lovgivningsmæssige og driftsmæssige krav.
          </P>
          <P>
            Formålet er at give deltagerne et fagligt afsæt for at navigere i forskellige
            traumeforståelser og reflektere over, hvilke perspektiver og handlemuligheder det åbner
            for i deres egen organisation.
          </P>
          <P>
            Introduktionen kan tilrettelægges som et kortere oplæg eller en hel kursusdag,
            afhængigt af jeres behov og hvor I er i processen.
          </P>
        </Example>

        <Example
          title="Kursus i traumebevidst tilgang"
          subline="Målgruppe: Alle organisationstyper og faggrupper. Særligt relevant for blandt andet bosteder, daginstitutioner, asylcentre, behandlingsenheder og kommunale afdelinger."
          testId="course-example-traumebevidst-tilgang"
        >
          <P>
            Et praksisnært kursusforløb, hvor vi arbejder med traumer, traumereaktioner og
            traumebevidst tilgang, og hvordan denne viden kan omsættes til praksis i mødet med dem,
            vi er sat i verden for at hjælpe, i samarbejdet med kolleger og i organisationens
            rammer.
          </P>
          <P>
            Vi introducerer til, hvad traumer er, hvordan traumereaktioner kan komme til udtryk og
            forstås, og hvordan denne viden kan bruges i praksis. Vi arbejder desuden med, hvordan
            vi kan skabe helhedsorienterede indsatser og styrke samarbejdet på tværs af fagligheder
            og sektorer.
          </P>
          <P>
            En vigtig del af kurset handler om os selv som fagpersoner. Hvordan bliver vi mere
            opmærksomme på os selv, den anden og den situation, vi står i? Hvad bringer vi selv med
            ind i mødet, hvordan reagerer vi på det, vi møder, og hvordan kan vi styrke vores
            faglige sikkerhed og handlekraft – også i situationer, der opleves som svære?
          </P>
          <P>
            Vi arbejder blandt andet med samtaler om traumer, vold, svigt og andre smertefulde
            erfaringer samt med, hvordan sprog og vores faglige forståelser kan påvirke menneskers
            muligheder for sikkerhed, forbindelse og handlekraft.
          </P>
          <P>
            Erfaringsperspektivet inddrages i samspil med den faglige viden, så vi også får
            indsigt i, hvordan vores praksis kan opleves af de mennesker, den vedrører.
          </P>
          <P>
            Undervejs arbejder vi med at omsætte viden og principper til deltagernes egen praksis.
            Vi undersøger konkrete situationer, faglige dilemmaer og handlemuligheder med
            udgangspunkt i de rammer og vilkår, deltagerne arbejder under.
          </P>
          <P>
            Målet er at styrke medarbejdernes vidensgrundlag og forudsætninger for traumebevidst
            praksis.
          </P>
          <h4 className="pt-2 font-serif text-lg text-[#3F5A47]">Tilrettelæggelse</h4>
          <P>
            Kurset kan sammensættes af forskellige moduler og tilrettelægges efter jeres behov,
            målgruppe, faglige opgaver og organisatoriske rammer. Det kan gennemføres som enkelte
            kursusdage eller som et længere sammenhængende forløb med mulighed for refleksion og
            afprøvning i egen praksis undervejs.
          </P>
        </Example>

        <Example
          title="Traumer og relationer"
          subline="Målgruppe: Særligt relevant for blandt andet social- og sundhedsfaglige medarbejdere, psykologer og psykoterapeuter, pædagoger og andre, der arbejder med omsorg og behandling."
          testId="course-example-moede"
        >
          <P>
            En mere dybdegående undervisning i, hvordan traumereaktioner kan komme til udtryk i
            relationer, og hvad der sker i samspillet mellem mennesker. Vi har særligt fokus på
            relationen mellem fagpersoner og dem, de søger at hjælpe, samt på relationer mellem
            kolleger. Vi undersøger, hvordan begge parters erfaringer, reaktioner og forståelser
            kan påvirke det, der sker i mødet.
          </P>
          <P>
            Vi arbejder blandt andet med forsvarsreaktioner, adfærdens funktion og de processer,
            der kan sættes i gang hos begge parter. Hvordan påvirker vores egne reaktioner og
            forståelser det, vi får øje på, og de muligheder, vi har for at handle i situationen?
          </P>
          <P>
            Der er særligt fokus på, hvordan vi kan være til stede i samtaler om traumer, vold,
            svigt og andre smertefulde erfaringer på måder, der understøtter sikkerhed, forbindelse
            og handlekraft og mindsker risikoen for at forstærke undgåelse og skam.
          </P>
          <P>
            Undervisningen kan gennemføres i et samspil mellem en psykologfaglig og en
            erfaringsfaglig underviser. Det giver mulighed for at forbinde faglig viden med et
            indefraperspektiv og konkret vise, hvordan traumebevidste samtaler om svære emner kan
            foregå.
          </P>
          <h4 className="pt-2 font-serif text-lg text-[#3F5A47]">Tilrettelæggelse</h4>
          <P>
            Undervisningen kan tilrettelægges som to enkelte kursusdage eller et længerevarende
            forløb.
          </P>
        </Example>

        <Example
          title="Forebyggelse af sekundær traumatisering og vikarierende stress"
          subline="Målgruppe: Alle fagpersoner, ledere og organisationer, der arbejder med mennesker i belastede livssituationer. Særligt relevant inden for socialområdet, sundhedsvæsenet, psykiatrien, asylsystemet, behandlingsområdet."
          testId="course-example-mennesker"
        >
          <P>
            Fagpersoner møder lidelse, svære fortællinger og stærke reaktioner med vores eget
            psykologiske og kropslige system. Det påvirker os. På godt og ondt.
          </P>
          <P>
            Vi giver viden om sekundær traumatisering, vikarierende stress og andre former for
            belastning, der kan opstå i arbejdet med mennesker. Vi undersøger, hvordan belastninger
            kan komme til udtryk, og hvordan vi kan genkende og forholde os til vores egne signaler
            og reaktioner.
          </P>
          <P>
            Jo bedre vi lykkes med at skabe traumebevidste miljøer præget af sikkerhed og tillid,
            desto større mulighed kan der også være for, at mennesker deler deres erfaringer med
            traumer og andre smertefulde oplevelser. På samme tid kan vi blive berørt, når vi
            oplever, hvordan mennesker møder svigt, afmagt og modgang i de systemer, der skulle
            hjælpe dem.
          </P>
          <P>
            Vi arbejder med konkrete muligheder for at forebygge og håndtere belastning før, under
            og efter møder med de mennesker, vi forsøger at hjælpe. Et særligt fokus er, hvordan vi
            kan være til stede i selve mødet og bevare kontakten til både os selv, den anden og
            situationen, også når vi bliver følelsesmæssigt berørt eller udfordret. Derudover ser
            vi på betydningen af kollegiale fællesskaber, ledelse og organisatoriske rammer for at
            forebygge belastning og understøtte et bæredygtigt arbejdsliv.
          </P>
          <P>
            Formålet er både at mindske risikoen for sekundær traumatisering og vikarierende stress
            og at styrke vores forudsætninger for at indgå i de meningsfulde samtaler og møder, som
            kan gøre arbejdet både berigende og meningsfuldt.
          </P>
          <h4 className="pt-2 font-serif text-lg text-[#3F5A47]">Tilrettelæggelse</h4>
          <P>
            Undervisningen kan tilrettelægges som en enkelt kursusdag eller et længere forløb med
            mulighed for fordybelse, refleksion og arbejde med konkrete situationer og rammer fra
            deltagernes egen praksis.
          </P>
        </Example>
      </div>
    </Section>

    <ContactCta title="Skal vi finde det rigtige format?">
      <P>
        Undervisning kan tilrettelægges som enkeltstående oplæg og kursusdage eller som en del af
        et længere forløb med eksempelvis supervision og implementeringsstøtte. Kontakt os gerne,
        hvis I vil drøfte målgruppe, temaer og hvad der vil give mening hos jer.
      </P>
    </ContactCta>

    <BackLink />
  </SubpageLayout>
);

export default Kurser;
